<div class="field-container" [formGroup]="parentFormGroup">
  <!-- Special handling for Instruction fields - no label -->
  <ng-container *ngIf="isInstructionField(); else normalField">
    <div class="instruction-content" [innerHTML]="getSafeInstructionContent()"></div>
  </ng-container>

  <ng-template #normalField>
    <label [for]="fieldKey" class="field-label">
      {{ field.label }}
      <span *ngIf="field.required && !field.readOnly" class="required-asterisk">*</span>
    </label>

    <ng-container [ngSwitch]="effectiveFieldType">
      <!-- Text input -->
      <input
        *ngSwitchCase="'text'"
        pInputText
        [id]="fieldKey"
        [formControlName]="fieldKey"
        [disabled]="isDisabled"
        [placeholder]="field['placeholder'] || ''"
        [attr.maxlength]="maxLengthText"
        class="w-full"
        [class.p-invalid]="isInvalid"
      />

      <!-- Textarea -->
      <textarea
        *ngSwitchCase="'textarea'"
        pTextarea
        [id]="fieldKey"
        [formControlName]="fieldKey"
        [disabled]="isDisabled"
        [placeholder]="field['placeholder'] || ''"
        [attr.maxlength]="maxLengthTextarea"
        class="w-full"
        [class.p-invalid]="isInvalid"
        rows="3"
      ></textarea>

      <!-- Number -->
      <p-inputNumber
        *ngSwitchCase="'number'"
        [id]="fieldKey"
        [formControlName]="fieldKey"
        [disabled]="isDisabled"
        [placeholder]="field['placeholder'] || ''"
        class="w-full"
        [class.p-invalid]="isInvalid"
        [mode]="field['mode'] === 'Decimal' ? 'decimal' : field['mode'] === 'Currency' ? 'currency' : undefined"
        [minFractionDigits]="field['mode'] === 'Decimal' ? 2 : undefined"
        [maxFractionDigits]="field['mode'] === 'Decimal' ? 5 : undefined"
        [currency]="field['mode'] === 'Currency' ? 'USD' : undefined"
        [locale]="field['mode'] === 'Currency' ? 'en-US' : undefined"
      ></p-inputNumber>

      <!-- Date -->
      <app-date-picker
        *ngSwitchCase="'date'"
        [id]="fieldKey"
        [formControlName]="fieldKey"
        [disabled]="isDisabled"
        [placeholder]="field['placeholder'] || ''"
        class="w-full"
        [invalid]="isInvalid"
        appendTo="body"
      ></app-date-picker>

      <!-- Time -->
      <p-datepicker
        *ngSwitchCase="'time'"
        [id]="fieldKey"
        [formControlName]="fieldKey"
        [disabled]="isDisabled"
        [showIcon]="true"
        [iconDisplay]="'input'"
        [timeOnly]="true"
        [placeholder]="field['placeholder'] || ''"
        class="w-full"
        [class.p-invalid]="isInvalid"
        appendTo="body"
      >
        <ng-template pTemplate="inputicon" let-clickCallBack="clickCallBack">
          <i class="pi pi-clock" (click)="clickCallBack($event)"></i>
        </ng-template>
      </p-datepicker>

      <!-- Single Choice -->
      <ng-container *ngSwitchCase="'single_choice'">
        <app-custom-single-choice
          *ngIf="!field.required"
          [options]="fieldOptions"
          [formControlName]="fieldKey"
          [disabled]="isDisabled"
          [layout]="field['layout'] || 'horizontal'"
          [fieldKey]="fieldKey"
          [index]="index"
        ></app-custom-single-choice>

        <div *ngIf="field.required" [class]="'field-radio-group' + fieldLayoutClass">
          <div *ngFor="let option of fieldOptions; let i = index; trackBy: trackByOptionIndex" class="field-radio-item">
            <p-radioButton
              [value]="option.value"
              [formControl]="formControl!"
              [name]="uniqueRadioName"
              [disabled]="isDisabled"
              [inputId]="fieldKey + '_' + index + '_' + i"
            ></p-radioButton>
            <label [for]="fieldKey + '_' + index + '_' + i">{{ option.label }}</label>
          </div>
        </div>
      </ng-container>

      <!-- Multiple Choice -->
      <div *ngSwitchCase="'multiple_choice'" [class]="'field-checkbox-group' + fieldLayoutClass">
        <div *ngFor="let option of fieldOptions; let i = index; trackBy: trackByOptionValue" class="field-checkbox-item">
          <p-checkbox [value]="option.value" [formControlName]="fieldKey" [disabled]="isDisabled" [inputId]="fieldKey + '_' + index + '_' + option.value"></p-checkbox>
          <label [for]="fieldKey + '_' + index + '_' + option.value">{{ option.label }}</label>
        </div>
      </div>

      <!-- Dropdown -->
      <p-select
        *ngSwitchCase="'dropdown'"
        [id]="fieldKey"
        [options]="fieldOptions"
        [formControlName]="fieldKey"
        [disabled]="isDisabled"
        [placeholder]="field['placeholder'] || ''"
        optionLabel="label"
        optionValue="value"
        class="w-full"
        [class.p-invalid]="isInvalid"
        appendTo="body"
        [showClear]="!field.required"
      ></p-select>

      <!-- Yes/No -->
      <ng-container *ngSwitchCase="'yes_no'">
        <app-custom-yes-no
          *ngIf="!field.required"
          [formControlName]="fieldKey"
          [disabled]="isDisabled"
          [fieldKey]="fieldKey"
          [index]="index"
        ></app-custom-yes-no>

        <div *ngIf="field.required" class="field-radio-group">
          <div class="flex gap-3">
            <div class="field-radio-item">
              <p-radioButton [value]="true" [formControl]="formControl!" [name]="uniqueRadioName" [disabled]="isDisabled" [inputId]="fieldKey + '_' + index + '_yes'"></p-radioButton>
              <label [for]="fieldKey + '_' + index + '_yes'">Yes</label>
            </div>
            <div class="field-radio-item">
              <p-radioButton [value]="false" [formControl]="formControl!" [name]="uniqueRadioName" [disabled]="isDisabled" [inputId]="fieldKey + '_' + index + '_no'"></p-radioButton>
              <label [for]="fieldKey + '_' + index + '_no'">No</label>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- Country -->
      <p-select
        *ngSwitchCase="'country'"
        [id]="fieldKey"
        [formControlName]="fieldKey"
        [disabled]="isDisabled"
        [options]="countryOptions"
        [placeholder]="field['placeholder'] || ''"
        optionLabel="label"
        optionValue="value"
        class="w-full"
        [class.p-invalid]="isInvalid"
        [appendTo]="'body'"
        [showClear]="!field.required"
      ></p-select>
    </ng-container>

    <div class="help-text" *ngIf="field['helpText']" [innerHTML]="getSafeHelpTextContent()"></div>

    <!-- Field Error Message -->
    <small *ngIf="fieldError" class="text-red-500">
      {{ fieldError }}
    </small>
  </ng-template>
</div>
